// client/src/components/pages/LexiStar/Approvals/ApprovalsPhone.tsx
//
// Deviation Approvals on a phone (ST-00207, m4b-contracts §5). Two screens on one route:
//
//  - The queue: one card per deviation (severity, SLA, registration, route, off-route distance,
//    "Driver replied") with a chevron; the whole card opens it.
//  - One deviation (?d= / ?deviation=, which is what a chat link names): "‹ Queue" and ‹ › to
//    move through the queue, the summary with its SLA, the driver's reply (photos included), the
//    map as a 260 px panel with a full-screen toggle, the trip, and the decision. Approve /
//    Reject / Escalate sit in the sticky bottom bar (PhoneDecisionBar); choosing one opens the
//    form above the bar, where Approve also offers the temporary corridor. (The support-ticket
//    button steps aside while the bar shows — PhoneLayout.css.)
//
// The page (Approvals.tsx) owns the data, the URL and the decisions; these components only lay
// them out. Keyboard shortcuts stay (a tablet with a keyboard).

import { useEffect, useMemo, useRef } from 'react';
import { Link } from 'react-router-dom';
import { FaCheck, FaTimes, FaLevelUpAlt, FaReply, FaChevronLeft, FaChevronRight } from 'react-icons/fa';
import PhoneCard, { PhoneCardList } from '../../../common/PhoneCard';
import PhoneActionBar, { PhoneAction } from '../../../common/PhoneActionBar';
import PhoneMapPanel, { PHONE_MAP_CANVAS } from '../../../common/PhoneMapPanel';
import { revealNode } from '../../../common/phoneLayout';
import { driverPhotosOf } from '../../../../services/lexistarMedia';
import { cardClasses } from '../StatusBoard/atoms';
import { fmtNum, fmtKm, fmtDuration, fmtTime, secondsSince } from '../StatusBoard/format';
import { TONES } from '../StatusBoard/statusLanguage';
import { slaCountdown } from './approvalLogic';
import useRouteGeometries from './useRouteGeometries';
import {
  CorridorMap,
  DecisionPanel,
  DriverReplyContent,
  SeverityChip,
  TripContextList,
  decisionStatusNote,
  registrationOf,
  type ActionId,
  type DecideFn,
  type DeviationRow,
  type ReasonCode,
} from './approvalParts';

const toneOf = (row: DeviationRow, overdue: boolean) =>
  overdue || row.severity === 'C' ? ('critical' as const) : row.severity === 'H' ? ('warning' as const) : null;

/** The queue as cards, most severe then oldest first (the page sorts). */
export function PhoneApprovalsQueue({
  rows,
  now,
  onOpen,
}: {
  rows: DeviationRow[];
  now: number;
  onOpen: (id: string) => void;
}) {
  return (
    <section aria-label="Queue" className="space-y-2">
      <h2 className="px-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">
        {rows.length} deviation{rows.length === 1 ? '' : 's'} · most severe first
      </h2>
      <PhoneCardList label="Approvals queue">
        {rows.map((r) => {
          const sla = slaCountdown(r, now);
          const reg = registrationOf(r);
          return (
            <PhoneCard
              key={r.id}
              title={<span className="font-mono">{reg}</span>}
              onOpen={() => onOpen(r.id)}
              openLabel={`${reg} deviation`}
              tone={toneOf(r, sla.overdue)}
              aside={
                <span className={`font-semibold ${sla.overdue ? 'text-red-700' : 'text-slate-600'}`} title="Time left to decide">
                  SLA {sla.text}
                </span>
              }
              dataAttributes={{ 'data-deviation': r.id }}
            >
              <div className="flex flex-wrap items-center gap-2">
                <SeverityChip severity={r.severity} />
                <span className="text-xs text-slate-500 tabular-nums">
                  opened {fmtDuration(secondsSince(r.opened_at, now))} ago
                </span>
              </div>
              <div className="text-slate-700">
                {r.trip?.from_name} → {r.trip?.to_name} · {fmtKm(r.off_route_km, 1)} off route
              </div>
              {!!r.driver_reason && (
                <span className="self-start inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-emerald-50 text-emerald-700 text-xs font-semibold">
                  <FaReply size={10} aria-hidden="true" /> Driver replied
                </span>
              )}
            </PhoneCard>
          );
        })}
      </PhoneCardList>
    </section>
  );
}

const navButton =
  'touch-target inline-flex items-center justify-center gap-1.5 px-3 text-sm font-semibold text-slate-700 bg-surface border border-slate-300 rounded-xl disabled:opacity-40';

/** One deviation, laid out for a phone; the decision bar is rendered by the page, last. */
export function PhoneDeviationDetail({
  row,
  index,
  total,
  onBack,
  onMove,
  canDecide,
  codes,
  pending,
  onApproveMode,
  onCancel,
  onDecide,
  busy,
  decisionError,
  now,
}: {
  row: DeviationRow;
  index: number;
  total: number;
  onBack: () => void;
  onMove: (delta: number) => void;
  canDecide: boolean;
  codes: ReasonCode[];
  pending: ActionId | null;
  onApproveMode: (action: 'approve' | 'approve_corridor') => void;
  onCancel: () => void;
  onDecide: DecideFn;
  busy: boolean;
  decisionError: string | null;
  now: number;
}) {
  const { lines, corridors, failed } = useRouteGeometries(
    // The hook skips a ref without a version (useRouteGeometries.js `wanted`).
    (row.trip?.route ? [{ route_id: row.trip.route.id, route_version: row.trip.route.version }] : []) as Array<{
      route_id: string;
      route_version: number;
    }>
  );
  const photos = useMemo(() => driverPhotosOf(row), [row]);
  const reg = registrationOf(row);
  const sla = slaCountdown(row, now);
  const status = decisionStatusNote({ row, canDecide, codes });
  const formRef = useRef<HTMLDivElement | null>(null);

  // A chosen action opens its form above the bar: bring it into view.
  useEffect(() => {
    if (pending) revealNode(formRef.current);
  }, [pending]);

  const stats: Array<[string, string]> = [
    ['Max off route', `${fmtNum(row.max_offset_m)} m`],
    ['Off-route distance', fmtKm(row.off_route_km, 1)],
    ['Elapsed', fmtDuration(secondsSince(row.opened_at, now))],
    ['Stops while off route', String((row.stops || []).length)],
  ];

  return (
    <div className="space-y-3" data-deviation-detail="">
      <nav className="flex items-center justify-between gap-2" aria-label="Queue">
        <button type="button" onClick={onBack} className={navButton}>
          <FaChevronLeft size={12} aria-hidden="true" /> Queue
          <span className="font-normal text-slate-500 tabular-nums">({total})</span>
        </button>
        <div className="flex items-center gap-1.5">
          <span className="px-1 text-sm text-slate-600 tabular-nums">
            {index + 1} of {total}
          </span>
          <button type="button" onClick={() => onMove(-1)} disabled={index <= 0} aria-label="Previous deviation" className={navButton}>
            <FaChevronLeft size={12} aria-hidden="true" />
          </button>
          <button type="button" onClick={() => onMove(1)} disabled={index >= total - 1} aria-label="Next deviation" className={navButton}>
            <FaChevronRight size={12} aria-hidden="true" />
          </button>
        </div>
      </nav>

      <section className={`${cardClasses} p-4`} aria-label="Deviation">
        <div className="flex items-start justify-between gap-3">
          <h2 className="text-lg font-bold text-slate-900 leading-snug min-w-0">
            <span className="font-mono">{reg}</span>
            <span className="text-slate-500 font-normal"> · {row.trip?.driver_name || row.trip?.driver_tag || 'No driver'}</span>
          </h2>
          <span
            className={`flex-shrink-0 mt-1 text-sm tabular-nums font-semibold ${sla.overdue ? 'text-red-700' : 'text-slate-600'}`}
            title="Time left to decide"
          >
            SLA {sla.text}
          </span>
        </div>
        <p className="text-sm text-slate-600 mt-0.5">
          <Link to={`/lexistar/trips/${row.trip_id}`} className="font-mono underline decoration-slate-300 underline-offset-2">
            {row.trip?.reference}
          </Link>{' '}
          · {row.trip?.route?.name || `${row.trip?.from_name} → ${row.trip?.to_name}`}
          {row.trip?.route?.version != null && ` (v${row.trip.route.version})`}
        </p>
        <div className="mt-2 flex flex-wrap items-center gap-2">
          <SeverityChip severity={row.severity} />
          <span className="px-2 py-0.5 rounded bg-slate-100 text-slate-700 text-xs font-medium">
            {row.state.replace(/_/g, ' ').toLowerCase()}
          </span>
          {row.confidence != null && (
            <span className="text-xs text-slate-500" title="Confidence the deviation is real">
              {row.confidence}% confidence
            </span>
          )}
        </div>
        <dl className="mt-3 grid grid-cols-2 gap-3 text-sm">
          {stats.map(([label, value]) => (
            <div key={label}>
              <dt className="text-xs text-slate-500 uppercase tracking-wider">{label}</dt>
              <dd className="font-semibold text-slate-900 tabular-nums">{value}</dd>
            </div>
          ))}
        </dl>
      </section>

      <section className={`${cardClasses} p-4`} aria-label="Driver reply">
        <h3 className="text-sm font-semibold text-slate-900 mb-2">Driver's reply</h3>
        <DriverReplyContent row={row} codes={codes} photos={photos} />
      </section>

      <PhoneMapPanel
        title="Map"
        subject={reg}
        className={`${cardClasses} p-4`}
        footer={
          (failed || !row.track || row.position) && (
            <>
              {(failed || !row.track) && (
                <span className="block">
                  {failed && 'The planned route could not be loaded. '}
                  {!row.track && 'No off-route track has been received for this deviation yet.'}
                </span>
              )}
              {row.position && (
                <span className="block">
                  Last position {fmtTime(row.position.ts)}
                  {row.position.speed_kmh != null && ` · ${Math.round(row.position.speed_kmh)} km/h`}
                </span>
              )}
            </>
          )
        }
      >
        <CorridorMap
          idPrefix="approvals"
          routes={lines}
          corridors={corridors}
          track={row.track}
          deviations={row.track ? [{ id: row.id, geometry: row.track, approved: row.state === 'APPROVED' }] : []}
          position={row.position}
          positionColor={TONES.loaded.hex}
          stops={row.stops || []}
          className={PHONE_MAP_CANVAS}
        />
      </PhoneMapPanel>

      <section className={`${cardClasses} p-4`} aria-label="Trip context">
        <h3 className="text-sm font-semibold text-slate-900 mb-2">Trip</h3>
        <TripContextList row={row} />
      </section>

      <section className={`${cardClasses} p-4`} aria-label="Decision">
        {status ??
          (pending ? (
            <div ref={formRef} className="scroll-mb-28">
              <DecisionPanel
                key={`${row.id}:${pending === 'approve_corridor' ? 'approve' : pending}`}
                phone
                action={pending}
                row={row}
                codes={codes}
                busy={busy}
                error={decisionError}
                onCancel={onCancel}
                onConfirm={onDecide}
                onApproveMode={onApproveMode}
              />
            </div>
          ) : (
            <p className="text-sm text-slate-600">
              Approve, reject or escalate with the buttons at the bottom of the screen. Every decision needs a reason.
            </p>
          ))}
      </section>
    </div>
  );
}

/** Approve / Reject / Escalate, under the thumb. */
export function PhoneDecisionBar({
  registration,
  pending,
  onChoose,
}: {
  registration: string;
  pending: ActionId | null;
  onChoose: (action: ActionId) => void;
}) {
  return (
    <PhoneActionBar label={`Decide on ${registration}`}>
      <PhoneAction
        variant="primary"
        icon={FaCheck}
        aria-pressed={pending === 'approve' || pending === 'approve_corridor'}
        onClick={() => onChoose('approve')}
      >
        Approve
      </PhoneAction>
      <PhoneAction icon={FaTimes} aria-pressed={pending === 'reject'} onClick={() => onChoose('reject')}>
        Reject
      </PhoneAction>
      <PhoneAction icon={FaLevelUpAlt} aria-pressed={pending === 'escalate'} onClick={() => onChoose('escalate')}>
        Escalate
      </PhoneAction>
    </PhoneActionBar>
  );
}
